import { useParams } from 'react-router-dom';
import {
  Check,
  X,
  ArrowUpRight,
  Shield,
  BarChart3,
  Building2,
  Briefcase,
  Map,
  Globe,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Lock,
  Clock,
  Award,
  Server,
  Headphones,
  Target,
  Landmark,
  Lightbulb,
  Palette,
  Heart,
  Bell,
  Smartphone,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

type FeaturePage = {
  title: string;
  description: string;
  keywords?: string;
  icon: any;
  eyebrow: string;
  headline: string;
  subhead: string;
  answer?: string;
  stats: { label: string; value: string; icon: any }[];
  pain: { title: string; points: string[] };
  benefits: { title: string; text: string; icon: any }[];
  useCases: { title: string; text: string }[];
  howItWorks: { step: number; title: string; text: string }[];
  sections: { title: string; text: string; icon?: any; points?: string[] }[];
  cards?: { title: string; text: string; icon: any }[];
  faqs: { q: string; a: string }[];
  cta?: { title: string; text: string };
  roadmap?: { quarter: string; title: string; text: string }[];
  heroImage?: string;
};

interface FeatureProps {
  slug?: string;
}

const featureMap: Record<string, FeaturePage> = {
  'quality-assurance': {
    title: 'Quality Assurance',
    description:
      'Score conversations, coach agents, and maintain consistent support quality with built-in QA tools inside ZuriHub.',
    keywords: 'quality assurance software, conversation scoring, agent coaching, QA rubric, support QA platform, SLA tracking, AI coaching',
    icon: Shield,
    eyebrow: 'Support excellence',
    headline: 'Make every conversation a coaching opportunity',
    subhead:
      'ZuriHub QA turns random reviews into a repeatable system. Score chats and tickets, track SLAs, and let Zyra AI find the coaching moments — all inside the same workspace.',
    answer:
      'ZuriHub Quality Assurance is a built-in workspace that lets support managers score conversations, track SLAs, and use Zyra AI to coach agents without switching tools.',
    stats: [
      { label: 'Faster QA reviews', value: '3x', icon: Clock },
      { label: 'Fewer repeat contacts', value: '-40%', icon: TrendingUp },
      { label: 'CSAT lift', value: '+18%', icon: Award },
    ],
    pain: {
      title: 'Most QA programs are too slow to help',
      points: [
        'Reviews happen once a month and miss the real issues',
        'Feedback lives in spreadsheets or Slack threads',
        'Managers cannot see trends across agents or channels',
      ],
    },
    benefits: [
      { title: 'Conversation scoring', text: 'Rate any chat, email, or ticket against custom rubrics you define.', icon: Target },
      { title: 'SLA tracking', text: 'First-response and resolution SLAs update automatically for every channel.', icon: Clock },
      { title: 'AI coaching', text: 'Zyra AI highlights tone, accuracy, and missed upsell opportunities.', icon: Lightbulb },
      { title: 'Team reports', text: 'Export QA dashboards by agent, team, or channel for leadership reviews.', icon: BarChart3 },
    ],
    useCases: [
      { title: 'Onboarding new agents', text: 'Use real conversations to train tone, accuracy, and speed.' },
      { title: 'Compliance reviews', text: 'Audit regulated industries with a clear, exportable review trail.' },
      { title: 'Performance reviews', text: 'Back up 1:1s with actual conversation scores and trends.' },
    ],
    howItWorks: [
      { step: 1, title: 'Build your rubric', text: 'Set criteria for tone, accuracy, empathy, or compliance.' },
      { step: 2, title: 'Sample conversations', text: 'Auto-sample by channel, team, or SLA breach.' },
      { step: 3, title: 'Score and coach', text: 'Leave inline feedback and share coaching moments instantly.' },
      { step: 4, title: 'Track progress', text: 'Watch agent and team scores improve over time.' },
    ],
    sections: [
      {
        title: 'Why QA matters',
        icon: Shield,
        text: 'Quality assurance is what separates reactive support from customer experiences that build loyalty. Teams with a structured QA process see higher CSAT, lower churn, and faster onboarding.',
        points: [
          'Identify knowledge gaps before they hurt customers',
          'Standardize tone and accuracy across agents',
          'Reduce repeat contacts by fixing root causes',
        ],
      },
      {
        title: 'How ZuriHub does QA',
        icon: Target,
        text: 'Review a conversation in seconds. Rate it on rubrics you define, add comments, and share feedback. Managers get dashboards by agent, team, and channel.',
        points: ['Inline feedback on any chat or ticket', 'Automated SLA breach alerts', 'AI summaries that surface coaching moments'],
      },
      {
        title: 'Scalable for any team size',
        icon: Users,
        text: 'Whether you have five agents or five hundred, QA stays lightweight. Sample conversations automatically, set review cycles, and export reports for leadership.',
        points: ['Bulk sampling and review cycles', 'Custom rubrics per team or channel', 'Exportable dashboards and PDF reports'],
      },
      {
        title: 'AI coaching at scale',
        icon: Lightbulb,
        text: 'Zyra AI reads conversations, flags missed opportunities, and suggests coaching actions. Agents get feedback while the context is still fresh, not weeks later.',
        points: ['Tone and accuracy alerts in real time', 'Missed upsell and compliance flags', 'Personalized coaching summaries'],
      },
    ],
    cards: [
      { title: 'Conversation scoring', text: 'Build custom rubrics and score any channel in seconds.', icon: Target },
      { title: 'SLA tracking', text: 'First-response and resolution targets update automatically.', icon: Clock },
      { title: 'AI coaching', text: 'Zyra AI finds coaching moments before they become habits.', icon: Lightbulb },
      { title: 'Team reports', text: 'Export leadership-ready QA dashboards by agent or team.', icon: BarChart3 },
    ],
    faqs: [
      { q: 'Do I need a separate QA tool?', a: 'No. ZuriHub includes QA scoring, coaching, and reporting inside the same platform.' },
      { q: 'Can I create custom scorecards?', a: 'Yes. Build rubrics for tone, accuracy, empathy, compliance, or any metric your team tracks.' },
      { q: 'Is it included in all plans?', a: 'QA tools are included in Pro and Enterprise plans.' },
    ],
    cta: { title: 'Build a support quality culture', text: 'See how ZuriHub QA works in a live workspace.' },
  },
  'road-map': {
    title: 'Road Map',
    description: 'See where ZuriHub is heading next. Our product roadmap is built around customer feedback, scale, and AI innovation.',
    icon: Map,
    eyebrow: 'What is next',
    headline: 'Building the future of CX, together',
    subhead:
      'We ship continuously. Every quarter we review customer requests, market trends, and platform stability — then publish what is coming.',
    stats: [
      { label: 'Workspaces launched', value: '250+', icon: Building2 },
      { label: 'Features shipped in 2026', value: '50+', icon: Zap },
      { label: 'Uptime achieved', value: '99.99%', icon: Server },
    ],
    pain: {
      title: 'SaaS roadmaps are usually a black box',
      points: [
        'You cannot tell if your feature request was heard',
        'Release dates are vague and slip repeatedly',
        'The product drifts away from real customer needs',
      ],
    },
    benefits: [
      { title: 'Public priorities', text: 'See which features are prioritized and why.', icon: Target },
      { title: 'Customer-driven', text: 'Backlog is shaped by the requests we see most often.', icon: Users },
      { title: 'Quarterly updates', text: 'Roadmap is reviewed and published every quarter.', icon: Clock },
      { title: 'Built in public', text: 'We share progress, challenges, and wins openly.', icon: Globe },
    ],
    useCases: [
      { title: 'Planning upgrades', text: 'See what is launching before you commit to a plan.' },
      { title: 'Feature requests', text: 'Submit and track ideas that matter to your team.' },
      { title: 'Strategic alignment', text: 'Align your CX strategy with our platform evolution.' },
    ],
    howItWorks: [
      { step: 1, title: 'Submit ideas', text: 'Tell us what would make ZuriHub better for your team.' },
      { step: 2, title: 'We prioritize', text: 'We rank requests by impact, demand, and platform fit.' },
      { step: 3, title: 'Quarterly plan', text: 'We publish the upcoming milestones publicly.' },
      { step: 4, title: 'Ship and review', text: 'We release, measure, and iterate with your feedback.' },
    ],
    sections: [
      {
        title: 'What we shipped in 2026',
        text: 'Launched in 2026 with a unified inbox supporting WhatsApp, Instagram, Telegram, Messenger, X, and email. Built comprehensive analytics, Zyra AI chatbot flows, Moments broadcast campaigns, knowledge base, ticket management, live chat widget, screen recording, heatmaps, and lead capture.',
      },
      {
        title: 'How we prioritize',
        text: 'Every quarter we review customer requests, market trends, and platform stability. The highest-impact, highest-need items move to the top. We prioritize features that serve our core mission: making world-class customer support affordable for every business.',
      },
      {
        title: 'Upcoming milestones',
        text: 'The next releases focus on AI voice calls, cloud call centre with number provisioning, native mobile apps, advanced Zyra Agent capabilities, and an open marketplace for integrations.',
      },
    ],
    faqs: [
      { q: 'Can I request a feature?', a: 'Yes. Reach out to support or your account manager to add ideas to our backlog. We review all requests and prioritize based on customer demand and strategic fit.' },
      { q: 'Do release dates change?', a: 'They can. We prefer to ship stable, complete features over hitting an arbitrary date. Quality and reliability are our top priorities.' },
      { q: 'Where is AI voice on the roadmap?', a: 'AI voice calls inside the inbox are in development. We are building custom moment flows for voice interactions, call recording, and transcription.' },
      { q: 'When will mobile apps launch?', a: 'Native iOS and Android apps for agents and supervisors are planned for 2027. In the meantime, our web workspace is fully responsive and mobile-optimized.' },
    ],
    cta: { title: 'Shape our roadmap', text: 'Start a free workspace and help us build the features you need.' },
    roadmap: [
      { quarter: 'Q4 2026', title: 'AI Voice Calls', text: 'Make and receive AI-assisted voice calls from the inbox with custom moment flows and transcription.' },
      { quarter: 'Q4 2026', title: 'Cloud Call Centre', text: 'Purchase numbers, run a dialpad, and manage enterprise call operations with hold, mute, transfer, and recording.' },
      { quarter: 'Q1 2027', title: 'Advanced Zyra Agent', text: 'Enhanced chatbot flows with memory, multi-turn conversations, and knowledge base integration.' },
      { quarter: 'Q1 2027', title: 'Native Mobile Apps', text: 'Full iOS and Android apps for agents and supervisors with push notifications and offline mode.' },
      { quarter: 'Q2 2027', title: 'Open Marketplace', text: 'Third-party integrations and custom connectors for CRM, e-commerce, and business tools.' },
      { quarter: 'Q2 2027', title: 'Multi-Workspace Analytics', text: 'Roll up visitor, ticket, and CSAT data across multiple workspaces for enterprise reporting.' },
      { quarter: 'Q3 2027', title: 'Quality Assurance Suite', text: 'Conversation scoring, AI coaching, QA rubrics, and compliance tracking for support teams.' },
      { quarter: 'Q3 2027', title: 'Advanced Automation', text: 'Workflow triggers, custom actions, and API webhooks for deeper integrations.' },
    ],
  },
  'zurihub-for-enterprise': {
    title: 'ZuriHub for Enterprise',
    description: 'Enterprise-grade security, SLAs, and AI for large support, sales, and operations teams.',
    keywords: 'enterprise customer support, SSO support, SCIM provisioning, SLA management, private cloud, AI voice, audit logs, ZuriHub enterprise',
    icon: Building2,
    eyebrow: 'Built for scale',
    headline: 'The support stack large teams can control',
    subhead:
      'ZuriHub for Enterprise adds the governance, security, and scale global organizations need: SSO, SCIM, custom SLAs, 99.99% uptime, private cloud, and AI voice.',
    answer:
      'ZuriHub for Enterprise is a secure, scalable support platform for large teams, with SSO, SCIM, custom SLAs, 99.99% uptime, private cloud, and AI voice.',
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/SMS-Feature-EN.avif',
    stats: [
      { label: 'Uptime SLA', value: '99.99%', icon: Server },
      { label: 'SSO support', value: 'SAML', icon: Lock },
      { label: 'Global edge', value: '<100ms', icon: Zap },
    ],
    pain: {
      title: 'Enterprise support tools create new silos',
      points: [
        'Channels and analytics are spread across different platforms',
        'Add-on pricing hides the real cost',
        'Security and compliance reviews take months',
      ],
    },
    benefits: [
      { title: 'SSO and SCIM', text: 'Connect your existing identity provider and automate onboarding.', icon: Lock },
      { title: 'Custom SLAs', text: 'Set, track, and alert on response and resolution targets.', icon: Clock },
      { title: 'AI voice', text: 'Make and transcribe calls inside the same inbox.', icon: Headphones },
      { title: 'Audit logs', text: 'Detailed records of every action for compliance.', icon: Shield },
    ],
    useCases: [
      { title: 'Global support operations', text: 'Route tickets by region, language, and business unit.' },
      { title: 'Financial services compliance', text: 'Meet audit and data-residency requirements.' },
      { title: 'Large-scale onboarding', text: 'Provision hundreds of agents through your IDP.' },
    ],
    howItWorks: [
      { step: 1, title: 'Security review', text: 'Share our SOC 2, GDPR, and architecture docs with your team.' },
      { step: 2, title: 'Custom rollout', text: 'We build a deployment plan matched to your org structure.' },
      { step: 3, title: 'SSO and data setup', text: 'Connect identity, map data residency, and set SLAs.' },
      { step: 4, title: 'Go live with support', text: 'A dedicated success contact helps you launch and scale.' },
    ],
    sections: [
      {
        title: 'Built for scale and compliance',
        icon: Building2,
        text: 'Enterprises need more than features. They need governance. ZuriHub for Enterprise adds role-based access, data residency, audit logs, and compliance-ready infrastructure.',
        points: ['SOC 2 and GDPR-ready controls', 'Data residency and private cloud options', 'Detailed audit trails for every action'],
      },
      {
        title: 'Voice and AI at enterprise depth',
        icon: Headphones,
        text: 'Use AI to answer, summarize, route, and coach. Add voice calls with real-time transcription and post-call analytics.',
        points: ['AI assistant trained on your docs', 'Voice calls in the same inbox', 'Call recording and searchable transcripts'],
      },
      {
        title: 'Support that matches your scale',
        icon: Users,
        text: 'Every Enterprise customer gets a dedicated success contact, priority support, and a custom rollout plan.',
        points: ['Dedicated customer success manager', 'Priority support and escalation', 'Custom onboarding and training'],
      },
      {
        title: 'Global and resilient',
        icon: Globe,
        text: 'ZuriHub runs on a global edge network with multi-region failover. Enterprise customers can choose data residency and private cloud options to meet local requirements.',
        points: ['Global edge with sub-100ms latency', 'Multi-region failover and backups', 'Data residency and private cloud options'],
      },
    ],
    cards: [
      { title: 'SSO and SCIM', text: 'Connect your identity provider and automate onboarding.', icon: Lock },
      { title: 'Custom SLAs', text: 'Set, track, and alert on response and resolution targets.', icon: Clock },
      { title: 'AI voice', text: 'Make and transcribe calls inside the same inbox.', icon: Headphones },
      { title: 'Audit logs', text: 'Detailed records of every action for compliance.', icon: Shield },
    ],
    faqs: [
      { q: 'Is there an uptime SLA?', a: 'Yes. Enterprise plans include a 99.99% uptime SLA.' },
      { q: 'Can I use my own identity provider?', a: 'Yes. SAML 2.0 and OIDC SSO are supported.' },
      { q: 'Do you offer private cloud?', a: 'Yes. Private cloud and on-premise deployment options are available for Enterprise.' },
    ],
    cta: { title: 'Talk to our enterprise team', text: 'Get a custom quote, security review, and rollout plan.' },
  },
  'zurihub-for-small-businesses': {
    title: 'ZuriHub for Small Businesses',
    description: 'Start free and scale. One platform for live chat, tickets, WhatsApp, AI, and analytics.',
    keywords: 'small business customer support, live chat for small business, WhatsApp business support, free help desk, AI customer support, small team CRM, ZuriHub small business',
    icon: Briefcase,
    eyebrow: 'Built for growth',
    headline: 'One platform that grows with your team',
    subhead:
      'Small teams do not have time for five different tools. ZuriHub gives you one inbox, one AI assistant, one knowledge base, and one analytics view — starting with a free agent plan.',
    answer:
      'ZuriHub for Small Businesses gives growing teams one inbox for live chat, WhatsApp, Instagram, email, and AI, with a free agent plan and transparent pricing.',
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/hero_business_startups__en_us.avif',
    stats: [
      { label: 'Free plan', value: '1 agent', icon: Users },
      { label: 'Pro price', value: '$25', icon: TrendingUp },
      { label: 'Setup time', value: 'Minutes', icon: Clock },
    ],
    pain: {
      title: 'Small teams are forced to buy bloated tools',
      points: [
        'Email, chat, and WhatsApp are in different inboxes',
        'AI and analytics cost extra on every other platform',
        'Annual contracts lock you in before you know what you need',
      ],
    },
    benefits: [
      { title: 'Free to start', text: 'One agent, core chat and ticket features, no setup fees.', icon: Users },
      { title: 'All channels', text: 'WhatsApp, Instagram, Telegram, email, and live chat in one place.', icon: Zap },
      { title: 'AI included', text: 'Zyra AI drafts replies and coaches agents on Pro.', icon: Lightbulb },
      { title: 'No annual lock-in', text: 'Add or remove agents monthly as you grow.', icon: Clock },
    ],
    useCases: [
      { title: 'Online stores', text: 'Answer product and order questions where customers ask.' },
      { title: 'Service providers', text: 'Book appointments, answer quotes, and close faster.' },
      { title: 'SaaS founders', text: 'Support early users without engineering overhead.' },
    ],
    howItWorks: [
      { step: 1, title: 'Create your workspace', text: 'Sign up and invite your team in under five minutes.' },
      { step: 2, title: 'Connect channels', text: 'Add live chat, WhatsApp, Instagram, or email.' },
      { step: 3, title: 'Train Zyra AI', text: 'Point Zyra at your help articles and common replies.' },
      { step: 4, title: 'Start supporting', text: 'Reply faster, close more tickets, and grow with data.' },
    ],
    sections: [
      {
        title: 'Why small businesses choose ZuriHub',
        icon: Briefcase,
        text: 'You can be up and running in an afternoon. Connect WhatsApp, Instagram, email, and chat. Let Zyra AI draft replies and your team closes tickets faster.',
        points: ['No setup fees or annual lock-in', 'Transparent per-agent pricing', 'Built-in knowledge base and live chat'],
      },
      {
        title: 'Pricing that grows with you',
        icon: TrendingUp,
        text: 'Start free. Upgrade to Pro at $25 per agent when you need more channels, users, or analytics. Heatmaps is just $15 per workspace.',
        points: ['One free agent forever', 'Add Pro agents at $25 per month', 'Optional heatmaps add-on'],
      },
      {
        title: 'Support without the enterprise complexity',
        icon: Zap,
        text: 'You get the same AI, the same channels, and the same reliability as enterprise customers — without the overhead.',
        points: ['No IT team required', 'Setup in minutes, not weeks', 'AI and channels from day one'],
      },
      {
        title: 'Get started in minutes',
        icon: Clock,
        text: 'Create a workspace, add a chat widget, and connect your messaging channels. Most small teams are supporting customers within an hour.',
        points: ['Sign up and invite your team', 'Connect WhatsApp, Instagram, or email', 'Train Zyra AI on your help content'],
      },
    ],
    cards: [
      { title: 'Free to start', text: 'One agent, core chat and ticket features, no setup fees.', icon: Users },
      { title: 'All channels', text: 'WhatsApp, Instagram, Telegram, email, and live chat in one place.', icon: Zap },
      { title: 'AI included', text: 'Zyra AI drafts replies and coaches agents on Pro.', icon: Lightbulb },
      { title: 'No lock-in', text: 'Add or remove agents monthly as you grow.', icon: Clock },
    ],
    faqs: [
      { q: 'Is there a free plan?', a: 'Yes. The free plan supports one agent with core chat and ticket features.' },
      { q: 'Can I add more agents later?', a: 'Yes. Add or remove Pro agents at any time.' },
      { q: 'What channels are included?', a: 'WhatsApp, Telegram, Instagram, email, live chat, and Facebook Messenger are available.' },
    ],
    cta: { title: 'Start free today', text: 'Create your workspace and chat with customers in minutes.' },
  },
  'push-notifications': {
    title: 'Push Notifications',
    description:
      'Engage customers in real-time with browser and mobile push notifications. Send scheduled campaigns, rich media attachments, and segmented messages across Chrome, Firefox, Safari, Android, and iOS.',
    keywords: 'push notifications, web push notifications, mobile push, browser notifications, customer engagement, real-time alerts, notification marketing, push API, scheduled notifications, rich media push, user segmentation, cross-platform push',
    icon: Bell,
    eyebrow: 'Real-time engagement',
    headline: 'Reach customers instantly, wherever they are',
    subhead:
      'ZuriHub Push Notifications enables businesses to send targeted, scheduled, and rich media push notifications to browsers and mobile applications. Engage users with personalized messages across Chrome, Firefox, Safari, Android, and iOS from one unified dashboard.',
    answer:
      'ZuriHub Push Notifications is a feature that lets businesses send browser and mobile push notifications with scheduling, rich media attachments, and user segmentation. Available as a $12/month add-on for unlimited sends.',
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/A%20PUSH.png',
    stats: [
      { label: 'Click-through rate', value: '4x', icon: TrendingUp },
      { label: 'Delivery speed', value: '<1s', icon: Zap },
      { label: 'User retention', value: '+25%', icon: Users },
      { label: 'Platform support', value: '6+', icon: Globe },
    ],
    pain: {
      title: 'Email alone is not enough for engagement',
      points: [
        'Email open rates are declining and inboxes are crowded',
        'Customers miss important updates buried in email threads',
        'No way to reach users in real-time for time-sensitive offers',
        'Unable to send rich media that captures attention',
        'No scheduling automation for optimal send times',
      ],
    },
    benefits: [
      { title: 'Browser push', text: 'Send notifications to Chrome, Firefox, Safari, Opera, Brave, and Opera Mini without email.', icon: Globe },
      { title: 'Mobile push', text: 'Reach users on iOS and Android apps with native push notifications.', icon: Smartphone },
      { title: 'Scheduled campaigns', text: 'Schedule notifications in advance for optimal delivery times.', icon: Clock },
      { title: 'Rich media attachments', text: 'Include images, videos, and interactive content in notifications.', icon: Palette },
      { title: 'Smart segmentation', text: 'Segment users by behavior, location, device, and custom attributes.', icon: Target },
      { title: 'Real-time delivery', text: 'Messages arrive instantly with sub-second delivery times.', icon: Zap },
    ],
    useCases: [
      { title: 'Cart abandonment', text: 'Send reminders when customers leave items in their cart with time-sensitive offers.' },
      { title: 'Order updates', text: 'Notify customers about shipping, delivery, and order status with rich tracking info.' },
      { title: 'Promotional campaigns', text: 'Announce sales, discounts, and new product launches with eye-catching media.' },
      { title: 'Service alerts', text: 'Inform users about maintenance, downtime, or urgent updates in real-time.' },
      { title: 'Content publishing', text: 'Notify readers when new articles, videos, or content is published.' },
      { title: 'Event reminders', text: 'Send automated reminders for webinars, meetings, and upcoming events.' },
    ],
    howItWorks: [
      { step: 1, title: 'Request permission', text: 'Your website or app prompts users to allow push notifications with native browser prompts.' },
      { step: 2, title: 'Segment users', text: 'Create segments based on behavior, location, device type, or custom attributes.' },
      { step: 3, title: 'Create campaigns', text: 'Compose messages with rich media, deep links, and personalization.' },
      { step: 4, title: 'Schedule or send', text: 'Send immediately or schedule for optimal delivery times based on user time zones.' },
      { step: 5, title: 'Track performance', text: 'Monitor delivery, clicks, conversions, and engagement to optimize campaigns.' },
    ],
    sections: [
      {
        title: 'Why push notifications matter',
        icon: Bell,
        text: 'Push notifications cut through the noise of email and social media. They appear directly on user devices with higher open rates and faster response times, making them ideal for time-sensitive communications and re-engagement campaigns.',
        points: [
          'Higher engagement rates compared to email (4x click-through rates)',
          'Instant delivery for urgent communications',
          'Does not require users to be in an app or on a website',
          'Supports rich media for visual engagement',
        ],
      },
      {
        title: 'Browser push notifications',
        icon: Globe,
        text: 'Send notifications to Chrome, Firefox, Safari, Opera, Brave, and Opera Mini browsers. Users receive alerts even when your website is not open, helping you re-engage visitors and drive return traffic. Browser push works across desktop and mobile web browsers.',
        points: [
          'Supports Chrome, Firefox, Safari, Opera, Brave, and Opera Mini',
          'No app installation required for web users',
          'Users can opt out at any time through browser settings',
          'Native permission prompts for user consent',
        ],
      },
      {
        title: 'Mobile push notifications',
        icon: Smartphone,
        text: 'Reach users on iOS and Android devices with native push notifications. Perfect for apps that need to send updates, reminders, or promotional messages to keep users engaged. Mobile push supports background and foreground delivery with rich media.',
        points: [
          'Native iOS and Android support with FCM and APNS',
          'Rich media and deep linking capabilities',
          'Background and foreground delivery modes',
          'Action buttons for direct user interaction',
        ],
      },
      {
        title: 'Scheduled campaigns',
        icon: Clock,
        text: 'Schedule notifications in advance to send at optimal times based on user time zones, behavior patterns, or custom schedules. Ensure your messages arrive when users are most likely to engage, improving open rates and conversions.',
        points: [
          'Schedule by date, time, or recurring patterns',
          'Time zone-aware delivery for global audiences',
          'Behavior-based scheduling for optimal engagement',
          'Campaign calendar and queue management',
        ],
      },
      {
        title: 'Rich media attachments',
        icon: Palette,
        text: 'Include images, videos, emojis, and interactive content in your push notifications. Rich media captures attention and improves engagement by making messages more visually appealing and informative.',
        points: [
          'Support for images, videos, and animated GIFs',
          'Emoji and Unicode character support',
          'Action buttons for direct user interaction',
          'Large image and hero banner formats',
        ],
      },
      {
        title: 'Smart segmentation',
        icon: Target,
        text: 'Segment your audience based on behavior, location, device type, or custom attributes. Send personalized messages that resonate with each user segment for higher engagement and conversion rates.',
        points: [
          'Behavior-based segmentation (last visit, purchase history)',
          'Location-based targeting (country, city, region)',
          'Device and platform segmentation (iOS, Android, browser)',
          'Custom user attributes and tags',
        ],
      },
      {
        title: 'Smart prompting',
        icon: Lightbulb,
        text: 'AI-powered suggestion engine that recommends optimal send times, message content, and segmentation strategies based on historical performance data. Smart prompting helps you craft more effective campaigns that drive higher engagement.',
        points: [
          'AI-recommended send times based on user behavior',
          'Content suggestions for higher open rates',
          'A/B testing recommendations for message variants',
          'Predictive segmentation suggestions',
        ],
      },
      {
        title: 'In-app personalization',
        icon: Heart,
        text: 'Deliver personalized experiences within your app using push notification data. Trigger in-app messages, update content dynamically, and customize the user experience based on notification interactions and user preferences.',
        points: [
          'Trigger in-app messages from push interactions',
          'Dynamic content updates based on user actions',
          'Personalized onboarding flows from notification clicks',
          'Context-aware in-app experiences',
        ],
      },
      {
        title: 'Platform support',
        icon: Globe,
        text: 'ZuriHub Push Notifications supports all major browser and mobile platforms, ensuring you can reach your entire audience regardless of their preferred browser or device.',
        points: [
          'Browsers: Chrome, Firefox, Safari, Opera, Brave, Opera Mini',
          'Mobile: iOS (APNS) and Android (FCM)',
          'Desktop and mobile web compatibility',
          'Cross-platform unified management',
        ],
      },
    ],
    cards: [
      { title: 'Browser push', text: 'Send notifications to Chrome, Firefox, Safari, Opera, Brave, and Opera Mini.', icon: Globe },
      { title: 'Mobile push', text: 'Reach users on iOS and Android apps with native notifications.', icon: Smartphone },
      { title: 'Scheduled campaigns', text: 'Schedule notifications in advance for optimal delivery times.', icon: Clock },
      { title: 'Rich media', text: 'Include images, videos, and interactive content in notifications.', icon: Palette },
      { title: 'Smart segmentation', text: 'Segment users by behavior, location, device, and attributes.', icon: Target },
      { title: 'Smart prompting', text: 'AI-recommended send times and content suggestions.', icon: Lightbulb },
    ],
    faqs: [
      { q: 'Do users need to install an app?', a: 'No. Browser push notifications work without app installation on desktop and mobile browsers including Chrome, Firefox, Safari, Opera, Brave, and Opera Mini.' },
      { q: 'Can users opt out?', a: 'Yes. Users can disable push notifications at any time through their browser or device settings.' },
      { q: 'What platforms are supported?', a: 'We support Chrome, Firefox, Safari, Opera, Brave, Opera Mini for browsers, and iOS and Android for mobile apps.' },
      { q: 'How does scheduling work?', a: 'You can schedule notifications by date, time, or recurring patterns. Delivery is time zone-aware and based on user behavior for optimal engagement.' },
      { q: 'What is the pricing?', a: 'Push Notifications is available as a $12/month add-on for unlimited sends. No per-message fees.' },
      { q: 'Is it included in all plans?', a: 'Push Notifications is an optional add-on available on all plans for $12/month with unlimited sends.' },
    ],
    cta: { title: 'Start engaging with push', text: 'Add Push Notifications for $12/month and send unlimited push campaigns.' },
  },
  'reporting-and-analytics': {
    title: 'Reporting and Analytics',
    description:
      'Comprehensive support analytics with real-time dashboards for ticket volume, agent performance, customer satisfaction, SLA tracking, and response times — all in one workspace.',
    keywords: 'support analytics, ticket reporting, agent performance metrics, CSAT tracking, SLA monitoring, response time analytics, customer satisfaction reports, support team KPIs',
    icon: BarChart3,
    eyebrow: 'Support insights at a glance',
    headline: 'Measure what matters for your support team',
    subhead:
      'ZuriHub Reporting and Analytics gives you real-time visibility into ticket volume, agent performance, customer satisfaction scores, SLA compliance, and response times. Track KPIs, identify trends, and make data-driven decisions to improve your support operations.',
    answer:
      'ZuriHub Reporting and Analytics is a comprehensive dashboard system that tracks ticket volume, agent performance, CSAT scores, SLA compliance, and response times in real-time.',
    stats: [
      { label: 'Real-time dashboards', value: '10+', icon: BarChart3 },
      { label: 'KPIs tracked', value: '25+', icon: Target },
      { label: 'SLA monitoring', value: 'Auto', icon: Clock },
    ],
    pain: {
      title: 'Support data is scattered and hard to act on',
      points: [
        'Ticket data, CSAT, and agent performance live in different tools',
        'Manual spreadsheets for SLA tracking and reporting',
        'No way to see trends or identify coaching opportunities',
      ],
    },
    benefits: [
      { title: 'Real-time dashboards', text: 'View ticket volume, agent performance, and CSAT in one place.', icon: BarChart3 },
      { title: 'SLA monitoring', text: 'Track first-response and resolution targets with automated alerts.', icon: Clock },
      { title: 'Agent performance', text: 'Measure response time, resolution rate, and customer satisfaction.', icon: Users },
      { title: 'Export reports', text: 'Generate PDF and CSV reports for leadership reviews.', icon: TrendingUp },
    ],
    useCases: [
      { title: 'Performance reviews', text: 'Back up 1:1s with data on response time, resolution rate, and CSAT.' },
      { title: 'SLA compliance', text: 'Monitor first-response and resolution targets with automated breach alerts.' },
      { title: 'Team optimization', text: 'Identify top performers and coaching opportunities across agents.' },
    ],
    howItWorks: [
      { step: 1, title: 'Configure metrics', text: 'Set KPIs for response time, resolution rate, CSAT, and SLAs.' },
      { step: 2, title: 'Track in real-time', text: 'Watch dashboards update automatically as tickets come in.' },
      { step: 3, title: 'Analyze trends', text: 'Filter by agent, team, channel, or time period to identify patterns.' },
      { step: 4, title: 'Export and share', text: 'Generate reports for leadership and share insights with the team.' },
    ],
    sections: [
      {
        title: 'Comprehensive dashboards',
        icon: BarChart3,
        text: 'All your support metrics in one place. Track ticket volume, agent performance, CSAT scores, SLA compliance, and response times with real-time dashboards that update automatically.',
        points: [
          'Ticket volume and channel breakdown',
          'Agent performance metrics',
          'Customer satisfaction scores',
          'SLA compliance tracking',
        ],
      },
      {
        title: 'SLA monitoring',
        icon: Clock,
        text: 'Set first-response and resolution targets for each channel. Track compliance in real-time and receive automated alerts when SLAs are at risk or breached.',
        points: [
          'First-response SLA tracking',
          'Resolution SLA monitoring',
          'Automated breach alerts',
          'SLA compliance reports',
        ],
      },
      {
        title: 'Agent performance',
        icon: Users,
        text: 'Measure individual and team performance with metrics for response time, resolution rate, ticket volume, and customer satisfaction. Identify top performers and coaching opportunities.',
        points: [
          'Response time tracking',
          'Resolution rate metrics',
          'Customer satisfaction by agent',
          'Coaching opportunity identification',
        ],
      },
      {
        title: 'Export and reporting',
        icon: TrendingUp,
        text: 'Generate leadership-ready reports in PDF and CSV formats. Schedule automated reports to keep stakeholders informed without manual work.',
        points: [
          'PDF and CSV export',
          'Scheduled automated reports',
          'Custom date range filtering',
          'Leadership-ready formatting',
        ],
      },
    ],
    cards: [
      { title: 'Real-time dashboards', text: 'View ticket volume, agent performance, and CSAT in one place.', icon: BarChart3 },
      { title: 'SLA monitoring', text: 'Track first-response and resolution targets with automated alerts.', icon: Clock },
      { title: 'Agent performance', text: 'Measure response time, resolution rate, and customer satisfaction.', icon: Users },
      { title: 'Export reports', text: 'Generate PDF and CSV reports for leadership reviews.', icon: TrendingUp },
    ],
    faqs: [
      { q: 'Is this included in all plans?', a: 'Reporting and Analytics dashboards are included in Pro and Enterprise plans.' },
      { q: 'Can I export reports?', a: 'Yes. You can export dashboards and reports in PDF and CSV formats.' },
      { q: 'Are SLA alerts automated?', a: 'Yes. SLA breach alerts are sent automatically when targets are at risk.' },
    ],
    cta: { title: 'Track your support performance', text: 'See how ZuriHub Reporting and Analytics can help your team.' },
  },
};

export default function Feature({ slug: propSlug }: FeatureProps = {}) {
  const { slug: routeSlug } = useParams();
  const slug = propSlug || routeSlug || '';
  const feature = featureMap[slug];

  if (!feature) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Feature not found</h1>
          <p className="text-slate-600">The feature you are looking for does not exist.</p>
        </div>
      </div>
    );
  }

  const { title, description, keywords, eyebrow, headline, subhead, stats, pain, benefits, useCases, howItWorks, sections, cards, faqs, cta, heroImage } = feature;

  return (
    <>
      <SEO
        title={`${title} - ZuriHub`}
        description={description}
        keywords={keywords}
        path={`/${slug}`}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djMxYzAgMS42NTctMS4zNDMgMy0zIDNIMzN2LTMxYzAtMS42NTcgMS4zNDMtMyAzLTNoNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="mb-6 inline-flex items-center justify-center rounded-full bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-400 ring-1 ring-blue-500/20">
                {eyebrow}
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl bg-gradient-to-r from-white via-blue-100 to-slate-200 bg-clip-text text-transparent">
                {headline}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-3xl">{subhead}</p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
                >
                  Book a Demo <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-blue-500 hover:bg-slate-800"
                >
                  View Pricing <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            {heroImage && (
              <div className="relative hidden lg:block">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 blur-3xl" />
                <div className="relative rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
                  <img src={heroImage} alt={title} className="rounded-2xl w-full h-auto" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center rounded-full bg-blue-100 p-3 mb-4">
                  <stat.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">{pain.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pain.points.map((point, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <X className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-slate-700">{point}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Key Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center rounded-lg bg-blue-100 p-3">
                    <benefit.icon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-slate-900">{benefit.title}</h3>
                  <p className="mt-2 text-slate-600">{benefit.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Use Cases</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{useCase.title}</h3>
                <p className="text-slate-600">{useCase.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white w-12 h-12 text-lg font-bold mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      {sections.map((section, index) => (
        <section key={index} className={`py-16 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="flex items-center mb-4">
                {section.icon && (
                  <div className="flex-shrink-0 mr-4">
                    <div className="inline-flex items-center justify-center rounded-lg bg-blue-100 p-3">
                      <section.icon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                )}
                <h2 className="text-3xl font-bold text-slate-900">{section.title}</h2>
              </div>
              <p className="mt-4 text-slate-600 text-lg leading-relaxed">{section.text}</p>
            </div>
            {section.points && (
              <ul className="mt-8 space-y-4">
                {section.points.map((point, i) => (
                  <li key={i} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {/* Cards */}
      {cards && (
        <section className="py-16 bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Features</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((card, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center rounded-lg bg-blue-100 p-3">
                        <card.icon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-slate-900">{card.title}</h3>
                      <p className="mt-2 text-slate-600">{card.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {cta && (
        <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">{cta.title}</h2>
            <p className="text-xl text-blue-100 mb-8">{cta.text}</p>
            <a
              href="/book-a-demo"
              className="inline-flex items-center justify-center rounded-lg bg-white px-8 py-4 text-blue-600 font-semibold hover:bg-blue-50 transition-all"
            >
              Book a Demo
              <ArrowUpRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </section>
      )}
    </>
  );
}
